import { Logout, Settings } from '@mui/icons-material';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import { AppBar, Box, Grid, Toolbar } from '@mui/material';
import { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Context } from '../../service/Context';
import { capitalizar, ordenarPorCategoria } from '../../utils/UtilesComun';
import { TypographyContrastApp } from '../custom/CustomComponents';
import { ButtonHeaderApp, ButtonNavegacionHeader } from '../custom/DedicatedComponents';

/**
 * Cabecera fija común a todas las páginas.
 * Contiene:
 * - Fila 1: Usuario, Club, Logo y Acciones (Configuración, Cerrar sesión).
 * - Fila 2: Barra secundaria con Panel de control, Scouting, Mi Club y Buscar.
 */
const Header = () => {
  const { idUsuario, idClub, cerrarSesion, usuarioNombre, clubNombre, obtenerEquiposClub, datosHomeCache } =
    useContext(Context);
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');

  const esPanelActivo = (location.pathname === '/home' || location.pathname === '/') && tabParam !== 'scouting';

  const esScoutingActivo =
    location.pathname === '/scouting' ||
    ((location.pathname === '/home' || location.pathname === '/') && tabParam === 'scouting');

  const currentRouteTeamId = location.pathname.startsWith('/equipo/') ? location.pathname.split('/')[2] : null;

  const esMiClubActivo = Boolean(
    location.pathname.startsWith('/equipo') &&
      currentRouteTeamId &&
      (datosHomeCache?.equiposClub?.length > 0
        ? datosHomeCache.equiposClub.some((eq) => String(eq.idEquipo) === String(currentRouteTeamId))
        : localStorage.getItem('ultimoEquipoClub') &&
          String(currentRouteTeamId) === String(localStorage.getItem('ultimoEquipoClub')))
  );

  const esBuscarActivo = location.pathname.startsWith('/explorar/ligas') || location.pathname.startsWith('/buscar');

  const handleNavegarMiClub = async () => {
    if (!idClub) {
      navigate('/home');
      return;
    }

    try {
      let equipos = datosHomeCache?.equiposClub;
      if (!equipos || equipos.length === 0) {
        equipos = await obtenerEquiposClub(idClub);
      }

      const equiposOrdenados = ordenarPorCategoria(equipos || []);
      const ultimoVisitado = localStorage.getItem('ultimoEquipoClub');

      // Validar que el último visitado realmente pertenece al club del usuario
      const esUltimoValido =
        ultimoVisitado && equiposOrdenados.some((eq) => String(eq.idEquipo) === String(ultimoVisitado));

      if (esUltimoValido) {
        navigate(`/equipo/${ultimoVisitado}`);
        return;
      }

      // Si no es válido o no existe, ir al equipo principal (primera categoría) del club propio
      if (equiposOrdenados.length > 0 && equiposOrdenados[0]?.idEquipo) {
        const idPrimerEquipo = String(equiposOrdenados[0].idEquipo);
        localStorage.setItem('ultimoEquipoClub', idPrimerEquipo);
        navigate(`/equipo/${idPrimerEquipo}`);
        return;
      }
    } catch (error) {
      console.error('Error al resolver equipo del club:', error);
    }
    navigate('/home');
  };

  const logoHeader = '/inteliscouting-logo-header.png';

  const estiloLogo = {
    height: '52px',
    width: 'auto',
  };

  if (!idUsuario) {
    return null;
  }

  return (
    <Box sx={{ marginBottom: 1 }}>
      <AppBar position="fixed">
        {/* First Row: User, Logo, Logout */}
        <Toolbar sx={{ minHeight: 64 }}>
          <Grid container alignItems="center" justifyContent="space-between">
            {/* Izquierda: Usuario y Club */}
            <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <TypographyContrastApp fontSize={16} fontWeight="bold">
                {usuarioNombre || 'Usuario'}
              </TypographyContrastApp>
              <TypographyContrastApp fontSize={13}>{capitalizar(clubNombre) || 'Club'}</TypographyContrastApp>
            </Grid>

            {/* Centro: Logo */}
            <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <img src={logoHeader} style={estiloLogo} alt="InteliScouting logo" />
            </Grid>

            {/* Derecha: Cerrar Sesión */}
            <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <ButtonHeaderApp onClick={() => navigate('/configuracion')} endIcon={<Settings />}>
                <TypographyContrastApp fontSize={13}>Configuración</TypographyContrastApp>
              </ButtonHeaderApp>
              <ButtonHeaderApp onClick={cerrarSesion} endIcon={<Logout />} exit>
                <TypographyContrastApp fontSize={13}>Cerrar sesión</TypographyContrastApp>
              </ButtonHeaderApp>
            </Grid>
          </Grid>
        </Toolbar>

        {/* Second Row: Barra de Navegación Estilo Pestañas */}
        <Box
          sx={{
            backgroundColor: '#d8eeeaff',
            borderBottom: '1px solid #b2ded7',
            boxShadow: '0 2px 6px rgba(15, 118, 110, 0.06)',
            minHeight: 46,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            px: { xs: 1, sm: 2 },
            pt: 0.5,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { height: 3 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#b2ded7', borderRadius: 3 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: { xs: 0.5, sm: 1 },
              position: 'relative',
              bottom: 0,
            }}
          >
            {/* Panel de control */}
            <ButtonNavegacionHeader
              startIcon={<DashboardOutlined />}
              onClick={() => navigate('/home?tab=panel')}
              isActive={esPanelActivo}
            >
              Panel de control
            </ButtonNavegacionHeader>

            {/* Scouting */}
            <ButtonNavegacionHeader
              startIcon={<AssessmentOutlined />}
              onClick={() => navigate('/home?tab=scouting')}
              isActive={esScoutingActivo}
            >
              Scouting
            </ButtonNavegacionHeader>

            {/* Mis Equipos */}
            <ButtonNavegacionHeader
              startIcon={<ShieldOutlined />}
              onClick={handleNavegarMiClub}
              isActive={esMiClubActivo}
            >
              Mis Equipos
            </ButtonNavegacionHeader>

            {/* Buscar */}
            <ButtonNavegacionHeader
              startIcon={<SearchIcon />}
              onClick={() => navigate('/explorar/ligas')}
              isActive={esBuscarActivo}
            >
              Buscar
            </ButtonNavegacionHeader>
          </Box>
        </Box>
      </AppBar>
    </Box>
  );
};

export default Header;
